/**
 * The network, without a network.
 *
 * Single-player opens one of these and plays. It is a real `Transport`: the same contract, the same
 * framing, the same lifecycle, with nobody on the other end. That is what keeps the game from
 * growing an "if we are online" branch, and it means every code path a player exercises alone is
 * the path multiplayer uses too.
 *
 * `createLoopbackPair` wires a host and a guest to each other in memory for tests. Delivery is
 * deferred through an injectable scheduler, so a test can hold messages and release them by hand,
 * which is how out-of-order and dropped snapshots get tested one day without a browser.
 */

import { decodeFrame, encodeFrame } from '@/net/wire/frameCodec';
import { IDLE_STATUS, nextStatus } from '@/net/session/connectionLifecycle';
import { NetError } from '@/net/session/sessionErrors';
import type {
  Channel,
  Transport,
  TransportOpenOptions,
  TransportPeer,
} from '@/net/transportContract';
import type { ConnectionStatus, PeerId, SessionRole } from '@/net/transportContract';
import type { MessageKind } from '@/net/wire/messageKinds';

/** The one strategy id the loopback reports, so a surface can say where a session came from. */
const LOOPBACK_STRATEGY = 'loopback';

const LOOPBACK_CODE = 'LOCAL';

export interface LoopbackPairOptions {
  /** Defaults to a microtask. A test passes a collector and flushes when it wants to. */
  readonly deliver?: (send: () => void) => void;
}

interface LoopbackWiring {
  readonly id: PeerId;
  readonly role: SessionRole;
  readonly joinCode: string;
  readonly options: TransportOpenOptions;
  readonly deliver: (send: () => void) => void;
}

interface LoopbackEnd extends Transport {
  /** Attaches the other end. Kept off `Transport`, which knows nothing about being paired. */
  connectTo(other: LoopbackEnd): void;
  receive(from: PeerId, bytes: Uint8Array, channel: Channel): void;
}

/** Single player: one transport, host role, no peers, and every send goes nowhere. */
export function createLoopbackTransport(options: TransportOpenOptions): Promise<Transport> {
  const end = createEnd({
    id: 'local',
    role: 'host',
    joinCode: LOOPBACK_CODE,
    options,
    deliver: queueMicrotask.bind(globalThis),
  });
  return Promise.resolve(end);
}

export interface LoopbackPair {
  readonly host: Transport;
  readonly guest: Transport;
  /**
   * Puts bytes straight onto an end, past the encoder. The only way to produce what a hostile or
   * much older peer would send, which is exactly what the decoder has to survive.
   */
  deliverRaw(to: 'host' | 'guest', bytes: Uint8Array, channel: Channel): void;
}

/** A host and a guest, wired to each other, both running the real codec. */
export async function createLoopbackPair(
  host: TransportOpenOptions,
  guest: TransportOpenOptions,
  options: LoopbackPairOptions = {},
): Promise<LoopbackPair> {
  const deliver = options.deliver ?? queueMicrotask.bind(globalThis);
  const hostEnd = createEnd({
    id: 'host',
    role: 'host',
    joinCode: LOOPBACK_CODE,
    options: host,
    deliver,
  });
  const guestEnd = createEnd({
    id: 'guest',
    role: 'guest',
    joinCode: LOOPBACK_CODE,
    options: guest,
    deliver,
  });

  hostEnd.connectTo(guestEnd);
  guestEnd.connectTo(hostEnd);
  return Promise.resolve({
    host: hostEnd,
    guest: guestEnd,
    deliverRaw(to: 'host' | 'guest', bytes: Uint8Array, channel: Channel): void {
      const end = to === 'host' ? hostEnd : guestEnd;
      end.receive(to === 'host' ? guestEnd.selfId : hostEnd.selfId, bytes, channel);
    },
  });
}

function createEnd(wiring: LoopbackWiring): LoopbackEnd {
  const { listeners } = wiring.options;
  let other: LoopbackEnd | null = null;
  let peer: TransportPeer | null = null;
  let status: ConnectionStatus = nextStatus(nextStatus(IDLE_STATUS, { kind: 'joinStarted' }), {
    kind: 'roomSettled',
    strategyId: LOOPBACK_STRATEGY,
  });
  let open = true;

  const announce = (signal: Parameters<typeof nextStatus>[1]): void => {
    const updated = nextStatus(status, signal);
    if (updated === status) return;
    status = updated;
    listeners.onStatus(status);
  };

  announce({ kind: 'peerReady', peerCount: 0 });

  return {
    selfId: wiring.id,
    role: wiring.role,
    joinCode: wiring.joinCode,

    status: (): ConnectionStatus => status,

    peers: (): readonly TransportPeer[] => (peer === null ? [] : [peer]),

    // Nothing in memory is ever late, and nothing in memory ever goes quiet, which is why the
    // loopback runs no heartbeat at all.
    rttOf: (): number | null => 0,

    send(channel: Channel, kind: MessageKind, payload: Uint8Array, target?: PeerId): void {
      if (!open || other === null) return;
      if (target !== undefined && target !== other.selfId) return;

      // Encoded here rather than at the far end, so a test that never opens a browser still proves
      // the bytes a peer would have parsed.
      const bytes = encodeFrame(kind, payload);
      const receiver = other;
      wiring.deliver(() => {
        receiver.receive(wiring.id, bytes, channel);
      });
    },

    receive(from: PeerId, bytes: Uint8Array, channel: Channel): void {
      if (!open) return;
      try {
        listeners.onMessage(from, decodeFrame(bytes), channel);
      } catch (cause) {
        listeners.onError(cause instanceof NetError ? cause : new NetError('malformedFrame'));
      }
    },

    connectTo(peerEnd: LoopbackEnd): void {
      other = peerEnd;
      peer = {
        id: peerEnd.selfId,
        role: peerEnd.role,
        snapshotChannelReady: true,
        relayed: false,
      };
      listeners.onPeerJoin(peer);
      announce({ kind: 'peerReady', peerCount: 1 });
    },

    close(reason: string): Promise<void> {
      if (!open) return Promise.resolve();
      open = false;
      if (peer !== null) listeners.onPeerLeave(peer.id, reason);
      peer = null;
      other = null;
      announce({ kind: 'closed' });
      return Promise.resolve();
    },
  };
}
